/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

import { auth, db } from "../firebase";

import {
  collectionGroup,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "actioned", label: "Actioned" },
  { value: "dismissed", label: "Dismissed" },
];

const RETRACTION_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const ECOLOGICAL_RELATIONSHIP_LABELS = {
  pollinates: "pollinates",
  visitsFlowersOf: "visits the flowers of",
  dispersesSeedsOf: "disperses the seeds of",
  consumesFruitsOf: "consumes the fruits of",
  consumesSeedsOf: "consumes the seeds of",
  preysOn: "preys on",
  feedsOn: "feeds on",
  parasitizes: "parasitizes",
  infects: "infects",
  decomposes: "decomposes",
  mycorrhizalWith: "has a mycorrhizal association with",
  mutualisticWith: "has a mutualistic relationship with",
  competesWith: "competes with",
};

const REASON_LABELS = {
  spam: "Spam or misleading content",
  harassment: "Harassment or abusive behavior",
  inappropriate: "Inappropriate content",
  privacy: "Privacy or personal information",
  copyright: "Copyright infringement",
  other: "Other",
};

function getTimestampMillis(timestamp) {
  return timestamp?.toMillis?.() || 0;
}

function formatReportDate(timestamp) {
  const date = timestamp?.toDate?.();

  if (!date) return "Pending timestamp";

  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getStatusClasses(status) {
  if (status === "actioned") {
    return "border-[#24C3BE]/30 bg-[#24C3BE]/10 text-[#01767A]";
  }

  if (status === "dismissed") {
    return "border-slate-200 bg-slate-100 text-slate-600";
  }

  return "border-amber-200 bg-amber-50 text-amber-700";
}

function getRetractionStatusClasses(status) {
  if (status === "approved") {
    return "border-[#24C3BE]/30 bg-[#24C3BE]/10 text-[#01767A]";
  }

  if (status === "rejected") {
    return "border-slate-200 bg-slate-100 text-slate-600";
  }

  return "border-amber-200 bg-amber-50 text-amber-700";
}

export default function AdminPage() {
  const functions = getFunctions(undefined, "southamerica-east1");
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyReportPath, setBusyReportPath] = useState("");
  const [contentStates, setContentStates] = useState({});
  const [retractionRequests, setRetractionRequests] = useState([]);
  const [retractionStatusFilter, setRetractionStatusFilter] =
    useState("pending");
  const [retractionsLoading, setRetractionsLoading] = useState(true);
  const [retractionsLoadError, setRetractionsLoadError] = useState("");
  const [busyRetractionPath, setBusyRetractionPath] = useState("");

  useEffect(() => {
    const reportsQuery = collectionGroup(db, "moderationReports");

    const unsubscribe = onSnapshot(
      reportsQuery,
      (snapshot) => {
        const loadedReports = snapshot.docs
          .map((reportDocument) => ({
            id: reportDocument.id,
            ref: reportDocument.ref,
            ...reportDocument.data(),
          }))
          .sort(
            (firstReport, secondReport) =>
              getTimestampMillis(secondReport.createdAt) -
              getTimestampMillis(firstReport.createdAt),
          );

        setReports(loadedReports);
        setLoadError("");
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load moderation reports:", error);
        setLoadError("The moderation reports could not be loaded.");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const retractionsQuery = collectionGroup(db, "retractionRequests");

    const unsubscribe = onSnapshot(
      retractionsQuery,
      (snapshot) => {
        const loadedRequests = snapshot.docs
          .map((requestDocument) => ({
            id: requestDocument.id,
            ref: requestDocument.ref,
            ...requestDocument.data(),
          }))
          .sort(
            (firstRequest, secondRequest) =>
              getTimestampMillis(secondRequest.requestedAt) -
              getTimestampMillis(firstRequest.requestedAt),
          );

        setRetractionRequests(loadedRequests);
        setRetractionsLoadError("");
        setRetractionsLoading(false);
      },
      (error) => {
        console.error(
          "Failed to load ecological relationship retractions:",
          error,
        );
        setRetractionsLoadError(
          "Ecological relationship retraction requests could not be loaded.",
        );
        setRetractionsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const reportCounts = useMemo(() => {
    return reports.reduce(
      (counts, report) => {
        const status = report.status || "pending";

        counts[status] = (counts[status] || 0) + 1;

        return counts;
      },
      {
        pending: 0,
        actioned: 0,
        dismissed: 0,
      },
    );
  }, [reports]);

  const visibleReports = useMemo(
    () =>
      reports.filter((report) => (report.status || "pending") === statusFilter),
    [reports, statusFilter],
  );

  const retractionCounts = useMemo(() => {
    return retractionRequests.reduce(
      (counts, request) => {
        const status = request.status || "pending";
        counts[status] = (counts[status] || 0) + 1;
        return counts;
      },
      { pending: 0, approved: 0, rejected: 0 },
    );
  }, [retractionRequests]);

  const visibleRetractionRequests = useMemo(
    () =>
      retractionRequests.filter(
        (request) =>
          (request.status || "pending") === retractionStatusFilter,
      ),
    [retractionRequests, retractionStatusFilter],
  );

  useEffect(() => {
    if (visibleReports.length === 0) {
      setContentStates({});
      return undefined;
    }

    let effectActive = true;
    const unsubscribers = [];

    setContentStates((currentStates) => {
      const nextStates = {};

      visibleReports.forEach((report) => {
        nextStates[report.ref.path] =
          currentStates[report.ref.path] || "checking";
      });

      return nextStates;
    });

    function updateContentState(reportPath, nextState) {
      if (!effectActive) return;

      setContentStates((currentStates) => ({
        ...currentStates,
        [reportPath]: nextState,
      }));
    }

    visibleReports.forEach((report) => {
      const reportPath = report.ref.path;

      if (!report.topicId) {
        updateContentState(reportPath, "deleted");
        return;
      }

      let topicState = "checking";
      let reportedItemState =
        report.contentType === "comment" ? "checking" : "available";

      function publishState() {
        if (topicState === "deleted" || reportedItemState === "deleted") {
          updateContentState(reportPath, "deleted");
          return;
        }

        if (topicState === "unknown" || reportedItemState === "unknown") {
          updateContentState(reportPath, "unknown");
          return;
        }

        if (topicState === "available" && reportedItemState === "available") {
          updateContentState(reportPath, "available");
        }
      }

      const topicRef = doc(db, "forumTopics", report.topicId);

      unsubscribers.push(
        onSnapshot(
          topicRef,
          (topicSnapshot) => {
            topicState =
              !topicSnapshot.exists() || topicSnapshot.data()?.deleted === true
                ? "deleted"
                : "available";

            publishState();
          },
          (error) => {
            console.error(
              "Failed to check reported topic availability:",
              error,
            );
            topicState = "unknown";
            publishState();
          },
        ),
      );

      if (report.contentType === "comment") {
        if (!report.commentId) {
          reportedItemState = "deleted";
          publishState();
          return;
        }

        const commentRef = doc(
          db,
          "forumTopics",
          report.topicId,
          "comments",
          report.commentId,
        );

        unsubscribers.push(
          onSnapshot(
            commentRef,
            (commentSnapshot) => {
              reportedItemState =
                !commentSnapshot.exists() ||
                commentSnapshot.data()?.deleted === true
                  ? "deleted"
                  : "available";

              publishState();
            },
            (error) => {
              console.error(
                "Failed to check reported comment availability:",
                error,
              );
              reportedItemState = "unknown";
              publishState();
            },
          ),
        );
      }
    });

    return () => {
      effectActive = false;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [visibleReports]);

  async function dismissReport(report) {
    const confirmed = window.confirm(
      "Dismiss this report without hiding the reported content?",
    );

    if (!confirmed || busyReportPath) return;

    setBusyReportPath(report.ref.path);
    setActionError("");

    try {
      await updateDoc(report.ref, {
        status: "dismissed",
        moderationAction: "no_action",
        reviewedAt: serverTimestamp(),
        reviewedByUid: auth.currentUser.uid,
      });
    } catch (error) {
      console.error("Failed to dismiss moderation report:", error);
      setActionError("The report could not be dismissed.");
    } finally {
      setBusyReportPath("");
    }
  }

  async function hideReportedContent(report) {
    const contentLabel = report.contentType === "comment" ? "comment" : "topic";

    const confirmed = window.confirm(
      `Hide this ${contentLabel} from BioIndex users?`,
    );

    if (!confirmed || busyReportPath) return;

    const contentRef =
      report.contentType === "comment"
        ? doc(db, "forumTopics", report.topicId, "comments", report.commentId)
        : doc(db, "forumTopics", report.topicId);

    setBusyReportPath(report.ref.path);
    setActionError("");

    try {
      await updateDoc(contentRef, {
        hidden: true,
        hiddenAt: serverTimestamp(),
        hiddenByUid: auth.currentUser.uid,
      });

      await updateDoc(report.ref, {
        status: "actioned",
        moderationAction: "content_hidden",
        reviewedAt: serverTimestamp(),
        reviewedByUid: auth.currentUser.uid,
      });
    } catch (error) {
      console.error("Failed to hide reported content:", error);
      setActionError(
        "The moderation action could not be completed. Check the content before retrying.",
      );
    } finally {
      setBusyReportPath("");
    }
  }

  async function reviewEcologicalRetraction(retractionRequest, decision) {
    const approving = decision === "approved";
    const confirmed = window.confirm(
      approving
        ? "Approve this request and retract the ecological relationship from BioIndex?"
        : "Reject this request and keep the ecological relationship unchanged?",
    );

    if (!confirmed || busyRetractionPath) return;

    setBusyRetractionPath(retractionRequest.ref.path);
    setActionError("");

    try {
      const reviewRetraction = httpsCallable(
        functions,
        "reviewEcologicalRelationshipRetraction",
      );

      await reviewRetraction({
        relationshipId: retractionRequest.relationshipId,
        requesterUid: retractionRequest.requesterUid,
        decision,
      });
    } catch (error) {
      console.error(
        "Failed to review ecological relationship retraction:",
        error,
      );
      setActionError(
        error?.message || "The retraction request could not be reviewed.",
      );
    } finally {
      setBusyRetractionPath("");
    }
  }

  return (
    <main className="min-h-screen bg-[#374049] px-3 py-5 sm:px-6 sm:py-8">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FDFBF8] shadow-2xl">
        <header className="border-b border-white/10 bg-[#374049] px-5 py-6 text-center sm:px-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#93CA87]">
            Restricted Access
          </p>

          <h1
            className="mt-1 text-4xl text-[#FCFFF6]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Admin Mode
          </h1>

          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-white/65">
            Review community reports, ecological retraction requests, and
            reported forum content.
          </p>
        </header>

        <div className="p-4 sm:p-7">
          <div className="mb-6">
            <Link
              to="/"
              className="text-sm font-semibold text-[#01767A] transition hover:text-[#374049]"
            >
              ← Return Home
            </Link>
          </div>

          <section className="mb-9 rounded-2xl border border-[#93CA87]/45 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#01767A]">
                  Taxonomy administration
                </p>
                <h2 className="mt-1 text-2xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>
                  Taxonomy Management
                </h2>
                <p className="mt-1 max-w-xl text-sm leading-6 text-[#374049]/60">
                  Prepare scientific-name changes, common-name updates, genus transfers, splits, and mergers.
                </p>
              </div>
              <Link
                to="/admin/taxonomy"
                className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[#01767A] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#374049]"
              >
                Open Taxonomy Management →
              </Link>
            </div>
          </section>

          <section className="mb-9">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#01767A]">
                  Species data moderation
                </p>
                <h2
                  className="mt-1 text-3xl text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  Ecological Relationship Retractions
                </h2>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-[#374049]/60">
                  Approving a request retracts the shared relationship for every
                  species view. Rejecting it leaves the relationship unchanged.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {RETRACTION_STATUS_OPTIONS.map((option) => {
                  const active = retractionStatusFilter === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setRetractionStatusFilter(option.value)}
                      aria-pressed={active}
                      className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                        active
                          ? "border-[#374049] bg-[#374049] text-white"
                          : "border-[#93CA87]/40 bg-white text-[#374049] hover:bg-[#93CA87]/10"
                      }`}
                    >
                      {option.label} ({retractionCounts[option.value] || 0})
                    </button>
                  );
                })}
              </div>
            </div>

            {retractionsLoading ? (
              <div className="rounded-2xl border border-[#93CA87]/30 bg-white px-5 py-10 text-center text-sm text-[#374049]/60">
                Loading ecological retraction requests...
              </div>
            ) : retractionsLoadError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-8 text-center text-sm text-red-700">
                {retractionsLoadError}
              </div>
            ) : visibleRetractionRequests.length === 0 ? (
              <div className="rounded-2xl border border-[#93CA87]/30 bg-white px-5 py-10 text-center">
                <p
                  className="text-2xl text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  No {retractionStatusFilter} retraction requests
                </p>
                <p className="mt-1 text-sm text-[#374049]/55">
                  Requests in this category will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {visibleRetractionRequests.map((retractionRequest) => {
                  const status = retractionRequest.status || "pending";
                  const busy =
                    busyRetractionPath === retractionRequest.ref.path;
                  const relationshipLabel =
                    ECOLOGICAL_RELATIONSHIP_LABELS[
                      retractionRequest.relationshipType
                    ] || retractionRequest.relationshipType;

                  return (
                    <article
                      key={retractionRequest.ref.path}
                      className="overflow-hidden rounded-2xl border border-[#93CA87]/35 bg-white shadow-sm"
                    >
                      <div className="border-b border-[#93CA87]/25 bg-[#FCFFF6] px-4 py-4 sm:px-5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${getRetractionStatusClasses(
                              status,
                            )}`}
                          >
                            {status}
                          </span>
                          <span className="text-xs text-[#374049]/50">
                            Requested {formatReportDate(
                              retractionRequest.requestedAt,
                            )}
                          </span>
                        </div>

                        <p className="mt-3 text-sm leading-relaxed text-[#374049]">
                          <span
                            className={
                              retractionRequest.sourceEntityType === "abiotic"
                                ? "font-medium"
                                : "italic"
                            }
                          >
                            {retractionRequest.sourceScientificName}
                          </span>{" "}
                          <span className="font-semibold text-[#01767A]">
                            {relationshipLabel}
                          </span>{" "}
                          <span
                            className={
                              retractionRequest.targetEntityType === "abiotic"
                                ? "font-medium"
                                : "italic"
                            }
                          >
                            {retractionRequest.targetScientificName}
                          </span>
                        </p>
                      </div>

                      <div className="grid gap-4 px-4 py-4 sm:grid-cols-2 sm:px-5">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                            Requested by
                          </p>
                          <p className="mt-1 text-sm text-[#374049]">
                            {retractionRequest.requesterName ||
                              "BioIndex contributor"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                            Community support
                          </p>
                          <p className="mt-1 text-sm text-[#374049]">
                            {retractionRequest.reportCount || 0}{" "}
                            {(retractionRequest.reportCount || 0) === 1
                              ? "report"
                              : "reports"}
                          </p>
                        </div>

                        <div className="sm:col-span-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                            Reason for retraction
                          </p>
                          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-[#374049]/75">
                            {retractionRequest.reason}
                          </p>
                        </div>
                      </div>

                      {status === "pending" && (
                        <div className="flex flex-col-reverse gap-2 border-t border-[#93CA87]/25 bg-[#FDFBF8] px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
                          <button
                            type="button"
                            onClick={() =>
                              reviewEcologicalRetraction(
                                retractionRequest,
                                "rejected",
                              )
                            }
                            disabled={busy}
                            className="rounded-xl border border-[#374049]/25 bg-white px-4 py-2.5 text-xs font-semibold text-[#374049] transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-50"
                          >
                            {busy ? "Working..." : "Reject Request"}
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              reviewEcologicalRetraction(
                                retractionRequest,
                                "approved",
                              )
                            }
                            disabled={busy}
                            className="rounded-xl bg-[#374049] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-[#01767A] disabled:cursor-wait disabled:opacity-50"
                          >
                            {busy ? "Working..." : "Approve Retraction"}
                          </button>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <div className="mb-4 border-t border-[#93CA87]/35 pt-7">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#01767A]">
              Community moderation
            </p>
            <h2
              className="mt-1 text-3xl text-[#374049]"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Forum Reports
            </h2>
          </div>

          <div className="mb-6 flex flex-wrap justify-end gap-2">
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((option) => {
                const active = statusFilter === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    aria-pressed={active}
                    className={`rounded-xl border px-4 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-[#374049] bg-[#374049] text-white"
                        : "border-[#93CA87]/40 bg-white text-[#374049] hover:bg-[#93CA87]/10"
                    }`}
                  >
                    {option.label} ({reportCounts[option.value] || 0})
                  </button>
                );
              })}
            </div>
          </div>

          {actionError && (
            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {actionError}
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl border border-[#93CA87]/30 bg-white px-5 py-12 text-center text-sm text-[#374049]/60">
              Loading moderation reports...
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-8 text-center text-sm text-red-700">
              {loadError}
            </div>
          ) : visibleReports.length === 0 ? (
            <div className="rounded-2xl border border-[#93CA87]/30 bg-white px-5 py-12 text-center">
              <p
                className="text-2xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                No {statusFilter} reports
              </p>

              <p className="mt-1 text-sm text-[#374049]/55">
                Reports in this category will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleReports.map((report) => {
                const status = report.status || "pending";
                const busy = busyReportPath === report.ref.path;
                const contentState =
                  contentStates[report.ref.path] || "checking";
                const contentDeleted = contentState === "deleted";

                return (
                  <article
                    key={report.ref.path}
                    className="overflow-hidden rounded-2xl border border-[#93CA87]/35 bg-white shadow-sm"
                  >
                    <div className="border-b border-[#93CA87]/25 px-4 py-4 sm:px-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${getStatusClasses(
                            status,
                          )}`}
                        >
                          {status}
                        </span>

                        {contentDeleted && (
                          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            Content deleted
                          </span>
                        )}

                        {contentDeleted ? (
                          <span
                            aria-disabled="true"
                            className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Discussion unavailable
                          </span>
                        ) : contentState === "checking" ? (
                          <span
                            aria-disabled="true"
                            className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                          >
                            Checking content
                          </span>
                        ) : (
                          <Link
                            to={`/forum/topic/${report.topicId}`}
                            className="rounded-full border border-[#93CA87]/40 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#01767A] transition hover:bg-[#93CA87]/10"
                          >
                            Open Discussion
                          </Link>
                        )}
                      </div>

                      <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                        {report.contentType === "comment"
                          ? "Forum Comment"
                          : "Forum Topic"}
                      </p>

                      <h2 className="mt-1 text-lg font-semibold text-[#374049]">
                        {report.contentTitle}
                      </h2>

                      <p className="mt-1 text-xs text-[#374049]/50">
                        Reported {formatReportDate(report.createdAt)}
                      </p>
                    </div>

                    <div className="space-y-4 px-4 py-4 sm:px-5">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                          Reported user
                        </p>

                        <p className="mt-1 text-sm text-[#374049]">
                          {report.reportedUserName}
                        </p>
                      </div>

                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                          Reason
                        </p>

                        <p className="mt-1 text-sm text-[#374049]">
                          {REASON_LABELS[report.reason] || report.reason}
                        </p>
                      </div>

                      {report.details && (
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                            Reporter details
                          </p>

                          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-[#374049]/75">
                            {report.details}
                          </p>
                        </div>
                      )}

                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45">
                          Reported content
                        </p>

                        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-[#374049]/75">
                          {report.contentBody}
                        </p>
                      </div>
                    </div>

                    {status === "pending" && (
                      <div className="flex flex-col-reverse gap-2 border-t border-[#93CA87]/25 bg-[#FDFBF8] px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
                        <button
                          type="button"
                          onClick={() => dismissReport(report)}
                          disabled={busy}
                          className="rounded-xl border border-[#374049]/25 bg-white px-4 py-2.5 text-xs font-semibold text-[#374049] transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-50"
                        >
                          {busy ? "Working..." : "Dismiss Report"}
                        </button>

                        <button
                          type="button"
                          onClick={() => hideReportedContent(report)}
                          disabled={busy}
                          className="rounded-xl bg-[#374049] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-[#2f373e] disabled:cursor-wait disabled:opacity-50"
                        >
                          {busy ? "Working..." : "Hide Content"}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
